import React, { useState, useEffect } from 'react';
import { Firestore } from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import '../../styles/AdminDashboard.css';

interface AdminDashboardProps {
  db: Firestore;
  functions: Functions;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({
  db,
  functions,
  onError,
  onSuccess,
  setLoading
}) => {
  const [totalUsers, setTotalUsers] = useState<number | null>(null);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);

  // Fetch total users on component mount
  useEffect(() => {
    fetchTotalUsers();
  }, []);

  const fetchTotalUsers = async () => {
    setLoadingUsers(true);
    try {
      const getTotalUsersFunction = httpsCallable(functions, 'getTotalUsers');
      const result = await getTotalUsersFunction();
      const data = result.data as { success: boolean; totalUsers: number; message: string };
      
      if (data.success) {
        setTotalUsers(data.totalUsers);
      }
    } catch (error: any) {
      console.error('Error fetching total users:', error);
      onError(error.message || 'Failed to fetch total users');
    } finally {
      setLoadingUsers(false);
    }
  };

  return (
    <div className="admin-dashboard">
      <h2>Admin Dashboard</h2>
      <p className="dashboard-subtitle">UNISON Conferences</p>

      <div className="dashboard-grid">
        {/* Total Users Card */}
        <div className="dashboard-card">
          <div className="card-header">
            <div className="card-icon users-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <h3>Total Users</h3>
          </div>
          
          <div className="card-body">
            {loadingUsers ? (
              <div className="card-loading">
                <div className="spinner-small"></div>
                <span>Loading...</span>
              </div>
            ) : (
              <>
                <div className="card-value">
                  {totalUsers !== null ? totalUsers.toLocaleString() : '-'}
                </div>
                <div className="card-description">
                  Registered authenticated users
                </div>
              </>
            )}
          </div>

          <div className="card-footer">
            <button 
              className="refresh-button"
              onClick={fetchTotalUsers}
              disabled={loadingUsers}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* Placeholder for future dashboard cards */}
        {/* <div className="dashboard-card placeholder">
          <div className="card-header">
            <div className="card-icon placeholder-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
            <h3>Coming Soon</h3>
          </div>
          <div className="card-body">
            <div className="card-value">-</div>
            <div className="card-description">More metrics coming soon</div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default AdminDashboard;